import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLang } from '../context/LangContext';
import SafeIcon from '../common/SafeIcon';
import { FiX, FiCheckCircle } from 'react-icons/fi';
import { cn } from '../utils/cn';

export default function OptionChoiceModal({
  isOpen,
  onClose,
  optionA,
  optionB,
  onConfirm,
  submitting = false,
}) {
  const { t } = useLang();
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!isOpen) setSelected(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const options = [
    { key: 'A', option: optionA },
    { key: 'B', option: optionB },
  ];

  const handleConfirm = () => {
    if (!selected || submitting) return;
    onConfirm(selected);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
      >
        <div className="p-6 border-b border-slate-800 flex justify-between items-center">
          <h2 className="text-xl font-black uppercase italic tracking-tighter">
            {t('select_option')}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <SafeIcon icon={FiX} size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-400 text-center">{t('select_option_desc')}</p>

          <div className="grid grid-cols-1 gap-3">
            {options.map(({ key, option }) => (
              <button
                key={key}
                type="button"
                disabled={submitting}
                onClick={() => setSelected(key)}
                className={cn(
                  'group flex items-center gap-4 w-full p-4 rounded-xl border-2 font-black uppercase tracking-wider transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
                  selected === key && 'ring-2 ring-white/30 scale-[1.02]'
                )}
                style={{
                  borderColor: option.color,
                  backgroundColor: `${option.color}${selected === key ? '30' : '15'}`,
                  color: option.color,
                }}
              >
                {option.image && (
                  <img
                    src={option.image}
                    alt={option.name}
                    className="w-12 h-12 rounded-full border-2 object-cover shrink-0"
                    style={{ borderColor: option.color }}
                  />
                )}
                <span className="flex-1 text-left text-lg">
                  {t('vote_for', option.name)}
                </span>
                <SafeIcon
                  icon={FiCheckCircle}
                  className={cn(
                    'shrink-0 transition-opacity',
                    selected === key ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'
                  )}
                />
              </button>
            ))}
          </div>

          <button
            type="button"
            disabled={!selected || submitting}
            onClick={handleConfirm}
            className="w-full py-4 bg-lime-400 disabled:bg-slate-800 disabled:text-slate-600 text-slate-950 font-black uppercase tracking-widest rounded-xl transition-all hover:scale-[1.02] disabled:hover:scale-100"
          >
            {submitting ? t('loading') : t('confirm_vote')}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
